import { TypedDocumentNode, gql } from '@apollo/client';
import { Outlet, generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineXL } from '@sorare/core/src/atoms/typography';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  FOOTBALL_PLAY_REWARDS_FIXTURE,
  MLB_PLAY_REWARDS_FIXTURE,
  NBA_PLAY_REWARDS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';

import FixtureSelector from 'components/fixture/FixtureSelector';
import { isFixtureClosed, isFixtureLive, isFixtureOpened } from 'lib/so5';

import { FixtureRewardsLayout_So5Fixture } from './__generated__/layout.graphql';

const Root = styled(Vertical)`
  padding: var(--double-unit) 0;
`;
const FixtureSelectorContainer = styled.div`
  max-width: 430px;
  margin: auto;
`;
const StyledHeadlineXL = styled(HeadlineXL)`
  font-weight: normal;
`;

const FIXTURE_REWARDS_URL = {
  [Sport.FOOTBALL]: FOOTBALL_PLAY_REWARDS_FIXTURE,
  [Sport.BASEBALL]: MLB_PLAY_REWARDS_FIXTURE,
  // FIXME use proper path once NBA is rails based
  [Sport.NBA]: NBA_PLAY_REWARDS,
};

type Props = {
  so5Fixture: FixtureRewardsLayout_So5Fixture;
};
export const FixtureRewardsLayout = ({ so5Fixture }: Props) => {
  const { sport } = useSportContext();

  if (!so5Fixture || !sport) {
    return null;
  }

  return (
    <Root gap={2}>
      <PageHeader text={glossary.rewardsPool} />
      <FullWidthContainer.NoPadding>
        <Vertical gap={2}>
          <Horizontal center>
            <StyledHeadlineXL as="p" className="text-center">
              {so5Fixture.displayName}
            </StyledHeadlineXL>
          </Horizontal>
          <FixtureSelectorContainer>
            <FixtureSelector
              withMask
              currentFixture={so5Fixture}
              navigateTo={fixture =>
                generatePath(FIXTURE_REWARDS_URL[sport], {
                  fixture,
                })
              }
            />
          </FixtureSelectorContainer>
        </Vertical>
      </FullWidthContainer.NoPadding>
      <Outlet
        context={{
          withActualRewards: isFixtureClosed(so5Fixture),
          withEligibleRewards: isFixtureLive(so5Fixture),
          withRewardsOverview: isFixtureOpened(so5Fixture),
        }}
      />
    </Root>
  );
};

FixtureRewardsLayout.fragments = {
  So5Fixture: gql`
    fragment FixtureRewardsLayout_So5Fixture on So5Fixture {
      slug
      displayName
      ...FixtureSelector_so5Fixture
      ...isFixtureClosed_so5Fixture
      ...isFixtureLive_so5Fixture
      ...isFixtureOpened_so5Fixture
    }
    ${FixtureSelector.fragments.so5Fixture}
    ${isFixtureClosed.fragments.so5Fixture}
    ${isFixtureLive.fragments.so5Fixture}
    ${isFixtureOpened.fragments.so5Fixture}
  ` as TypedDocumentNode<FixtureRewardsLayout_So5Fixture>,
};
